import type { ThemeUIStyleObject } from '@theme-ui/core';

import { RedditIcon } from '@change/ds-icons';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { Link } from '@change-corgi/design-system-legacy/components/actions';
import { Image } from '@change-corgi/design-system-legacy/components/media';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import type { Recommendation } from '../../../../hooks/useShareRecommendations';
import { useShareRecommendationsTracking } from '../../../../hooks/useShareRecommendationsTracking';

const styles: Record<string, ThemeUIStyleObject> = {
	defaultIconContainer: {
		alignItems: 'center',
		height: '100%',
		justifyContent: 'center',
		width: '100%',
	},
	cardContainer: {
		border: '1px solid',
		borderColor: 'neutral-grey400',
		borderRadius: '8px',
		height: '70px',
		maxWidth: [null, '430px'],
		width: '100%',
		/* eslint-disable-next-line @typescript-eslint/naming-convention */
		'&:hover': {
			boxShadow: '0px 10px 20px 0px #18191F1A',
			cursor: 'pointer',
		},
	},
	innerCardContainer: {
		backgroundColor: 'neutral-grey400',
		borderRadius: '8px 0px 0px 8px',
		flexShrink: 0,
		height: '100%',
		width: ['95px', '125px'],
	},
	imageContainer: {
		justifyContent: 'center',
		borderRadius: '7px 0px 0px 7px',
		height: '100%',
		overflow: 'hidden',
		width: '100%',
	},
	headingText: {
		flex: 1,
		flexDirection: 'column',
		justifyContent: 'center',
		height: '100%',
		paddingLeft: '16px',
		paddingRight: '16px',
		width: 'auto',
		gap: '4px',
	},
	blurredImageContainer: {
		width: ['95px', '125px'],
		height: '100%',
		position: 'absolute',
		alignItems: 'center',
		justifyContent: 'center',
	},
	blurredImage: {
		width: '192px', // 4 times the size of the icon
		height: '192px',
		zIndex: 0,
		filter: 'blur(16px)',
		objectFit: 'cover',
	},
	iconContainer: {
		position: 'relative',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1,
		width: '100%',
		height: '100%',
	},
	iconImage: { height: '48px', width: '48px', border: '2px solid white', borderRadius: '210px' },
};

type Props = {
	recommendation: Recommendation;
};

function defaultIconImage() {
	return (
		<div
			className="flex h-full w-full items-center justify-center bg-[#FFC7B2]"
			data-testid="subreddit-card-default-icon"
		>
			<div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-white bg-[#FF4500]">
				<RedditIcon className="text-white" size={24} />
			</div>
		</div>
	);
}

function imageContainer(imageUrl: string) {
	return (
		<Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
			<Flex sx={styles.blurredImageContainer}>
				<Image src={imageUrl} alt="" sx={styles.blurredImage} />
			</Flex>
			<Flex sx={styles.iconContainer}>
				<Image alt="" src={imageUrl} sx={styles.iconImage} data-testid="subreddit-card-image" />
			</Flex>
		</Box>
	);
}

/*
	Using blank alt attribute to prevent screen readers from reading the images. This is needed because the
	images are decorative and the whole component is wrapped in a link. Screen readers would announce twice or
	even three times the same information if we don't use blank alt attribute.
 */
export function SubredditCard({ recommendation }: Props) {
	const { metadata } = recommendation;
	const {
		actions: { handleLinkClick },
	} = useShareRecommendationsTracking();

	const { localizeNumber } = useI18n();

	return (
		<Link
			sx={{
				textDecoration: 'none',
			}}
			to={recommendation.url}
			target="_blank"
			onClick={() => {
				handleLinkClick(recommendation.name, 'reddit_group', recommendation.metadata.source);
			}}
			tabIndex={0}
			aria-label={`Subreddit: ${recommendation.name}`}
			data-testid="subreddit-card"
			data-qa="subreddit-card"
		>
			<Flex sx={styles.cardContainer}>
				<Box sx={styles.innerCardContainer}>
					{/* Simulate an inner border */}
					<Flex sx={styles.imageContainer}>
						{!metadata.imageUrl && defaultIconImage()}
						{metadata.imageUrl && imageContainer(metadata.imageUrl)}
					</Flex>
				</Box>
				<Flex sx={styles.headingText}>
					<Heading
						as="h5"
						size={['h6', 'h5']}
						color="typography-primary"
						lineClamp={1}
						sx={{ fontWeight: '700', letterSpacing: '-1%', lineHeight: '120%' }}
						data-testid="subreddit-card-heading"
					>
						{recommendation.name}
					</Heading>
					{metadata.memberCount && (
						<Text
							size="small"
							color="typography-secondary"
							sx={{ fontWeight: '400' }}
							data-testid="subreddit-card-member-count"
						>
							<Translate
								value="corgi.petition.starter_dashboard.recommendations.followers_count"
								replacements={{ followers_count: localizeNumber(metadata.memberCount) }}
							/>
						</Text>
					)}
				</Flex>
			</Flex>
		</Link>
	);
}
